// Copyright (c) Docugami, Inc. All rights reserved.

import { useState, useRef, useEffect } from 'react';
import { IButtonStyles, IconButton, MaskedTextField, Stack } from "@fluentui/react";
import { useSelector } from 'react-redux';
import React from 'react';
import { Telemetry } from 'telemetry/TelemetryManager';
import { PageFilmNumberInputedSchema, PageFilmThumbnailClickedSchema, TelemetryEvent } from 'telemetry/TelemetryEvents';
import { greyText } from 'components/shell/presentation/AppStyles';
import { useAppDispatch, useAppSelector } from 'hooks/redux';
import { dgFontFamily } from 'components/common/Fonts';
import styled from 'styled-components';
import { useGetDocumentPagesSpriteSheetQuery } from 'store/frontend/frontendSlice';
import { mapCurrentPageOnScreen } from 'store/state_helpers';
import { setCurrentPageOnScreen, setCurrentSelectedPage } from 'store/sdvSlice';

const PageNumberStack = styled(Stack)`
    font-family: ${dgFontFamily};
    font-size: 10px;
    margin-top: 8px;
    flex-direction: column !important;
`

const PagesFilmStripContainer = styled.div`
    grid-area: #{$grid-area-doc-view-filmstrip};
    overflow-y: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;

    display: flex;
    flex-direction: column;
    align-items: center;

    .pagesFilmStrip {
        height: 100%;
        width: 100%;
        overflow-y: hidden;
        overflow-x: hidden;
        flex: 1;

        > * {
            margin-left: auto;
            margin-right: auto;
        }

        &:hover {
            overflow-y: auto;
        }

        .pageContainer {
            height: 66px;
            width: 51px;
            border-style: solid;
            border-color: black;
            cursor: pointer;
            margin-top: -5px; /* changed from 10px to 5px */
        }

        .currentPageBorder {
            border: 4px solid #56CCF1;
            box-sizing: border-box;
            scale: 0.66;
        }

        .nonCurrentPageBorder {
            border: 1px solid #DADADA;
            box-sizing: border-box;
            scale: 0.66;
        }

        .pageNumber {
            width: 51px;
            text-align: center;
            margin-bottom: 5px;
        }
    }
`

const PageCountText = styled.div`
    padding-top: 3px;
`

const thumbnailWidth: number = 51;

export interface PagesFilmStripProps {
    documentId: string;
}

export const PagesFilmStrip = (props: PagesFilmStripProps): JSX.Element => {
    const [thumbnailRefs, setThumbnailRefs] = useState<React.RefObject<HTMLDivElement>[]>([]);
    const scrollerRef = useRef<HTMLDivElement>();

    const pageCount = useAppSelector(state => state.documents.indexed[props.documentId]?.clusterDocument.numberOfPages ?? 0);
    const curPageOnScreenIndex = useSelector(mapCurrentPageOnScreen);
    const { data: thumbnailSpritesUrl } = useGetDocumentPagesSpriteSheetQuery(props.documentId)

    const [desiredPageNumber, setDesiredPageNumber] = useState<string | undefined>(`${curPageOnScreenIndex + 1}`);

    const dispatch = useAppDispatch();

    const buttonStyle: IButtonStyles = {
        root: {
            width: thumbnailWidth,
            color: greyText
        }
    }

    useEffect(() => {
        let res = [...thumbnailRefs];
        for (let i = 0; i < pageCount - thumbnailRefs.length; i++) {
            res.push(React.createRef());
        }

        setThumbnailRefs(res);
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [pageCount]);

    useEffect(() => {
        setDesiredPageNumber(`${curPageOnScreenIndex + 1}`)
    }, [curPageOnScreenIndex])

    useEffect(() => {
        if (scrollerRef.current && curPageOnScreenIndex >= 0 &&
            curPageOnScreenIndex < thumbnailRefs.length &&
            thumbnailRefs[curPageOnScreenIndex].current) {
            const padding = 10;
            let thumbnail = thumbnailRefs[curPageOnScreenIndex].current;
            if (!thumbnail) {
                return;
            }

            let thumbnailTop = thumbnail.offsetTop - scrollerRef.current.offsetTop;
            let scrollTop = -1;

            if ((thumbnailTop - padding) < scrollerRef.current.scrollTop) {
                scrollTop = Math.max((thumbnailTop - padding + 5), 0);
            }
            else if (thumbnailTop > (scrollerRef.current.scrollTop + scrollerRef.current.clientHeight - thumbnail.clientHeight - padding)) {
                scrollTop = thumbnailTop - (scrollerRef.current.clientHeight - thumbnail.clientHeight - 3 * padding - 5);
            }

            if (scrollTop >= 0) {
                scrollerRef.current.scrollTo({
                    top: scrollTop,
                    behavior: "auto"
                });
            }
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [curPageOnScreenIndex])

    function goToPageNumber(pageNumberIndex: number) {
        dispatch(setCurrentSelectedPage(pageNumberIndex));
        dispatch(setCurrentPageOnScreen(pageNumberIndex));
    }

    function thumbnailClicked(pageNumberIndex: number) {
        Telemetry.trackEvent<PageFilmThumbnailClickedSchema>({
            pageNumber: pageNumberIndex,
            eventName: TelemetryEvent.PageFilmThumbnailClicked
        });
        goToPageNumber(pageNumberIndex);
    }

    function thumbnailBorder(pageNumberIndex: number): string {
        let res = "pageContainer";

        if (pageNumberIndex === curPageOnScreenIndex) {
            res = res + " currentPageBorder";
        } else {
            res = res + " nonCurrentPageBorder";
        }

        return res;
    }

    function pageThumbnails(): JSX.Element[] {
        let res: JSX.Element[] = [];

        if (!thumbnailSpritesUrl) {
            return res;
        }

        for (let i = 0; i < pageCount; i++) {
            res.push(<div
                title={`Page ${i + 1}`}
                ref={thumbnailRefs[i]}
                key={`thumbnail_${i}`}
                style={{ background: `url(${thumbnailSpritesUrl}) ${i * -thumbnailWidth}px 0px` }}
                className={thumbnailBorder(i)}
                onClick={() => thumbnailClicked(i)} />)
        }

        return res;
    }

    function gotoLast(): void {
        thumbnailClicked(pageCount - 1);
    }

    function gotoFirst(): void {
        thumbnailClicked(0);
    }

    const switchToDesiredNumber = () => {
        if (!!desiredPageNumber) {
            const newPageNumberIndex = Number.parseInt(desiredPageNumber ?? '1') - 1;

            if (newPageNumberIndex >= 0 && newPageNumberIndex < pageCount) {
                Telemetry.trackEvent<PageFilmNumberInputedSchema>({
                    pageNumber: newPageNumberIndex,
                    eventName: TelemetryEvent.PageFilmNumberInputed
                });
                goToPageNumber(newPageNumberIndex);
            }
        }
    }

    return (
        <PagesFilmStripContainer>
            <PageNumberStack horizontal verticalAlign='center'>
                <MaskedTextField
                    styles={{
                        root: {
                            width: 37,
                            fontFamily: dgFontFamily,
                            fontSize: '10px',
                            height: 23,
                            minHeight: 23,
                        },
                        fieldGroup: {
                            fontFamily: dgFontFamily,
                            fontSize: '10px',
                            height: 23,
                            minHeight: 23,
                        },
                        field: {
                            fontFamily: dgFontFamily,
                            fontSize: '10px',
                            height: 23,
                            minHeight: 23,
                        }
                    }}
                    maskChar={''}
                    mask={'999'}
                    value={desiredPageNumber}
                    onBlur={switchToDesiredNumber}
                    onKeyUp={(event) => {
                        if (event.key === 'Enter') {
                            switchToDesiredNumber();
                        }
                    }}
                    onChange={(event, newValue) => {
                        setDesiredPageNumber(newValue);
                    }} />
                <PageCountText>of {pageCount}</PageCountText>
            </PageNumberStack>
            {pageCount > 0 ?
                <IconButton
                    iconProps={{ iconName: 'ChevronUp' }}
                    onClick={gotoFirst}
                    styles={buttonStyle} /> : null}
            <div className="pagesFilmStrip" ref={scrollerRef as React.RefObject<HTMLDivElement>}>
                {pageThumbnails()}
            </div>
            {pageCount > 0 ?
                <IconButton
                    iconProps={{ iconName: 'ChevronDown' }}
                    onClick={gotoLast}
                    styles={buttonStyle} /> : null}
        </PagesFilmStripContainer>
    )
}
